import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateIST, formatTimeIST, todayDateIST } from '../../lib/dateUtils';
import { saveChunk, getSessionBlob, clearSession } from '../../lib/recordingStore';
import {
  ArrowLeft, Mic, Square, Pause, Play, Loader2,
  Plus, FileText, Stethoscope, CheckCircle, Clock, Phone,
  User, Pill, AlertTriangle, Sparkles, ChevronRight,
  ClipboardList, BrainCircuit, Download, Printer, RefreshCw,
  Pencil, Save, X, Trash2, Search, BookOpen, MessageSquare,
  Eye, ChevronDown, ChevronUp, MicOff, History, Lock, HelpCircle,
  Volume2, GraduationCap, Share2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  checkin: { label: 'Check-in', color: 'bg-[#E6EFEA] text-[#0E5132]' },
  history: { label: 'History', color: 'bg-amber-100 text-amber-700' },
  doctor_consult: { label: 'Consult', color: 'bg-violet-100 text-violet-700' },
  prescription: { label: 'Rx', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Done', color: 'bg-slate-100 text-slate-600' },
};

function StageProgress({ currentStage }) {
  const stages = ['checkin', 'history', 'doctor_consult', 'prescription', 'closed'];
  const currentIdx = stages.indexOf(currentStage);
  return (
    <div className="flex items-center gap-0.5" data-testid="stage-progress">
      {stages.map((stage, i) => {
        const conf = STAGE_CONFIG[stage];
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <div key={stage} className="flex items-center">
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
              isActive ? conf.color + ' ring-1 ring-offset-1 ring-blue-300' :
              isDone ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'
            }`}>
              {isDone && <CheckCircle className="w-2.5 h-2.5" />}
              {conf.label}
            </div>
            {i < stages.length - 1 && <ChevronRight className="w-2.5 h-2.5 text-slate-300 mx-0.5" />}
          </div>
        );
      })}
    </div>
  );
}

function formatFollowUpDate(val) {
  if (!val) return '';
  // If it's already a date string like "2026-07-10", format as DD-MM-YYYY
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  return val; // fallback for free text like "6 weeks"
}

function AudioPlayer({ src, label }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play().catch(() => {}); }
    setPlaying(!playing);
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
  };

  const onLoaded = () => {
    if (audioRef.current && isFinite(audioRef.current.duration)) setDuration(audioRef.current.duration);
  };

  const onEnded = () => setPlaying(false);

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * duration;
  };

  const fmtTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (!src) return null;
  const fullSrc = src.startsWith('http') ? src : `${API_URL}${src}`;

  return (
    <div className="flex items-center gap-2 p-1.5 rounded-lg bg-slate-50 border border-slate-200" data-testid="audio-player">
      <audio ref={audioRef} src={fullSrc} onTimeUpdate={onTimeUpdate} onLoadedMetadata={onLoaded} onEnded={onEnded} preload="metadata" />
      <Button type="button" variant="ghost" size="icon" onClick={toggle} className="h-6 w-6 rounded-full text-[#0E5132] hover:bg-emerald-100 flex-shrink-0">
        {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
      </Button>
      <div className="flex-1 min-w-0 cursor-pointer h-1.5 rounded-full bg-slate-200 relative" onClick={seek}>
        <div className="h-full rounded-full bg-[#0E5132] transition-all" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
      </div>
      <span className="text-[9px] text-slate-400 flex-shrink-0 tabular-nums">{fmtTime(progress)}/{fmtTime(duration)}</span>
      {label && <span className="text-[9px] text-slate-400 flex-shrink-0">{label}</span>}
    </div>
  );
}

function SessionRecorder({ cardId, sessionId, onComplete }) {
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [interimText, setInterimText] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const audioNodesRef = useRef({ context: null, processor: null, source: null, gain: null });
  const mountedRef = useRef(true);
  const liveBoxRef = useRef(null);
  const seqRef = useRef(0);
  const [failedBlob, setFailedBlob] = useState(null);
  const [recoveredBlob, setRecoveredBlob] = useState(null);

  // Recovery: detect an unsaved recording from a crashed/closed session
  useEffect(() => {
    getSessionBlob(sessionId).then(b => { if (b && b.size >= 100) setRecoveredBlob(b); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Warn before leaving the page mid-recording or with an un-uploaded recording
  useEffect(() => {
    const guard = (e) => { e.preventDefault(); e.returnValue = ''; };
    if (recording || failedBlob) {
      window.addEventListener('beforeunload', guard);
      return () => window.removeEventListener('beforeunload', guard);
    }
  }, [recording, failedBlob]);

  const uploadBlobWithRetry = async (blob, attempts = 3) => {
    for (let i = 0; i < attempts; i++) {
      try {
        const formData = new FormData();
        formData.append('file', blob, `session_${Date.now()}.webm`);
        await axios.post(
          `${API_URL}/api/consult-cards/${cardId}/sessions/${sessionId}/upload-audio`,
          formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return true;
      } catch (e) {
        console.warn(`Upload attempt ${i + 1} failed:`, e);
        if (i < attempts - 1) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
      }
    }
    return false;
  };

  const triggerProcessing = async () => {
    try {
      await axios.post(`${API_URL}/api/consult-cards/${cardId}/sessions/${sessionId}/process`, {}, { withCredentials: true });
      toast.success('Transcribing audio...');
    } catch { /* ok */ }
  };

  const retryUpload = async (blob, fromRecovery = false) => {
    setSaving(true);
    const ok = await uploadBlobWithRetry(blob);
    setSaving(false);
    if (ok) {
      clearSession(sessionId).catch(() => {});
      setFailedBlob(null);
      setRecoveredBlob(null);
      await triggerProcessing();
      onComplete?.();
    } else {
      toast.error(fromRecovery ? 'Upload failed again — recording is still saved on this device.' : 'Upload failed again — use Download to keep a copy, then retry.', { duration: 10000 });
    }
  };

  const downloadBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session_${sessionId}_${Date.now()}.webm`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (liveBoxRef.current) {
      liveBoxRef.current.scrollTop = liveBoxRef.current.scrollHeight;
    }
  }, [liveTranscript, interimText]);

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const cleanupAudio = () => {
    try {
      const nodes = audioNodesRef.current;
      if (nodes.processor) { nodes.processor.disconnect(); nodes.processor = null; }
      if (nodes.source) { nodes.source.disconnect(); nodes.source = null; }
      if (nodes.gain) { nodes.gain.disconnect(); nodes.gain = null; }
      if (nodes.context && nodes.context.state !== 'closed') {
        nodes.context.close().catch(() => {});
        nodes.context = null;
      }
    } catch (e) { console.warn('Audio cleanup:', e); }
  };

  const cleanupWebSocket = () => {
    try {
      if (wsRef.current) {
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close();
        }
        wsRef.current = null;
      }
    } catch (e) { console.warn('WS cleanup:', e); }
    setWsConnected(false);
  };

  const cleanupStream = () => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (e) { console.warn('Stream cleanup:', e); }
    streamRef.current = null;
  };

  const startRecording = async () => {
    try {
      setRecoveredBlob(null);
      await clearSession(sessionId).catch(() => {});
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      streamRef.current = stream;

      // MediaRecorder for backup audio (chunks also persisted to IndexedDB — crash-proof)
      seqRef.current = 0;
      const onChunk = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          saveChunk(sessionId, seqRef.current++, e.data).catch(() => {});
        }
      };
      try {
        const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 48000 });
        chunksRef.current = [];
        mr.ondataavailable = onChunk;
        mr.start(1000);
        mediaRecorderRef.current = mr;
      } catch (e) {
        console.warn('MediaRecorder fallback:', e);
        const mr = new MediaRecorder(stream);
        chunksRef.current = [];
        mr.ondataavailable = onChunk;
        mr.start(1000);
        mediaRecorderRef.current = mr;
      }

      // WebSocket for live transcription
      try {
        const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
        const ws = new WebSocket(`${wsUrl}/api/ws/transcribe/${sessionId}`);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!mountedRef.current) return;
          setWsConnected(true);
          try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
            const source = audioContext.createMediaStreamSource(stream);
            const processor = audioContext.createScriptProcessor(4096, 1, 1);
            const silentGain = audioContext.createGain();
            silentGain.gain.value = 0;

            source.connect(processor);
            processor.connect(silentGain);
            silentGain.connect(audioContext.destination);

            audioNodesRef.current = { context: audioContext, processor, source, gain: silentGain };

            processor.onaudioprocess = (e) => {
              if (ws.readyState === WebSocket.OPEN) {
                const input = e.inputBuffer.getChannelData(0);
                const pcm = new Int16Array(input.length);
                for (let i = 0; i < input.length; i++) {
                  pcm[i] = Math.max(-32768, Math.min(32767, Math.floor(input[i] * 32768)));
                }
                ws.send(pcm.buffer);
              }
            };
          } catch (e) {
            console.warn('AudioContext error:', e);
          }
        };

        ws.onmessage = (event) => {
          if (!mountedRef.current) return;
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'transcript') {
              if (msg.is_final) {
                setLiveTranscript(msg.full_transcript || '');
                setInterimText('');
              } else {
                setInterimText(msg.text || '');
              }
            }
          } catch { /* ignore */ }
        };

        ws.onerror = () => { if (mountedRef.current) setWsConnected(false); };
        ws.onclose = () => { if (mountedRef.current) setWsConnected(false); };
      } catch (e) {
        console.warn('WebSocket setup failed:', e);
      }

      setRecording(true);
      setPaused(false);
      setElapsed(0);
      setLiveTranscript('');
      setInterimText('');
      timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
    } catch (e) {
      toast.error('Microphone access denied');
      console.error('getUserMedia error:', e);
    }
  };

  const pauseRecording = () => {
    try { if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.pause(); } catch { /* ok */ }
    setPaused(true);
    clearInterval(timerRef.current);
  };

  const resumeRecording = () => {
    try { if (mediaRecorderRef.current?.state === 'paused') mediaRecorderRef.current.resume(); } catch { /* ok */ }
    setPaused(false);
    timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
  };

  // Auto-save guard: warn at 60 min, auto-stop & save at 65 min (keeps file under transcription size limits)
  const autoStopFiredRef = useRef(false);
  useEffect(() => {
    if (!recording) { autoStopFiredRef.current = false; return; }
    if (elapsed === 3600) {
      toast.warning('60 minutes recorded — the session will auto-save at 65 minutes. You can start a new session right after.', { duration: 8000 });
    }
    if (elapsed >= 3900 && !autoStopFiredRef.current) {
      autoStopFiredRef.current = true;
      toast.info('65-minute limit reached — auto-saving this session now. Start a new session to continue.', { duration: 8000 });
      stopRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed, recording]);

  const stopRecording = async () => {
    clearInterval(timerRef.current);
    setRecording(false);
    setPaused(false);
    setSaving(true);

    // 1. Cleanup audio processing
    cleanupAudio();

    // 2. Close WebSocket (backend auto-saves transcript)
    cleanupWebSocket();

    // 3. Stop MediaRecorder
    try {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== 'inactive') {
        await new Promise((resolve) => {
          mr.onstop = resolve;
          mr.stop();
        });
      }
    } catch (e) { console.warn('MediaRecorder stop:', e); }

    // 4. Stop mic
    cleanupStream();

    // 5. Upload audio — retries, never silently discards
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    let uploaded = false;
    if (blob.size >= 100) {
      uploaded = await uploadBlobWithRetry(blob);
      if (!uploaded) {
        setFailedBlob(blob);
        setSaving(false);
        toast.error('Recording saved locally — upload failed. Use "Retry Upload" below. Do not close this page until uploaded.', { duration: 12000 });
        return;
      }
      clearSession(sessionId).catch(() => {});
    }

    // 6. Auto-trigger processing — recorded audio is the source of truth
    // (full audio is re-transcribed with auto language detection; live transcript is only a preview)
    try {
      await new Promise(r => setTimeout(r, 300));
      const { data: status } = await axios.get(
        `${API_URL}/api/consult-cards/${cardId}/sessions/${sessionId}/status`,
        { withCredentials: true }
      );
      if (status.audio_url) {
        await axios.post(`${API_URL}/api/consult-cards/${cardId}/sessions/${sessionId}/process`, {}, { withCredentials: true });
        toast.success('Transcribing audio...');
      } else if (status.transcript) {
        await axios.post(`${API_URL}/api/consult-cards/${cardId}/sessions/${sessionId}/summarize`, {}, { withCredentials: true });
        toast.success('Generating summary...');
      }
    } catch { /* ok */ }

    setSaving(false);
    onComplete?.();
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearInterval(timerRef.current);
      cleanupAudio();
      cleanupWebSocket();
      cleanupStream();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (saving) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-xl bg-[#E6EFEA] border border-[rgba(14,81,50,0.15)]">
        <Loader2 className="w-4 h-4 animate-spin text-[#0E5132]" />
        <span className="text-sm text-[#0E5132]">Processing recording...</span>
      </div>
    );
  }

  if (!recording) {
    if (failedBlob) {
      return (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 space-y-2" data-testid="upload-failed-panel">
          <p className="text-xs font-medium text-red-700">Recording could not be uploaded — it is saved safely on this device.</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => retryUpload(failedBlob)} className="rounded-full text-xs gap-1 bg-red-600 hover:bg-red-700" data-testid="retry-upload-btn">
              <RefreshCw className="w-3 h-3" /> Retry Upload
            </Button>
            <Button size="sm" variant="outline" onClick={() => downloadBlob(failedBlob)} className="rounded-full text-xs" data-testid="download-recording-btn">
              Download Recording
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {recoveredBlob && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 space-y-2" data-testid="recovered-recording-panel">
            <p className="text-xs font-medium text-amber-700">
              Unsaved recording found from an interrupted session ({(recoveredBlob.size / (1024 * 1024)).toFixed(1)} MB). Upload it or discard.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => retryUpload(recoveredBlob, true)} className="rounded-full text-xs gap-1 bg-amber-600 hover:bg-amber-700" data-testid="recover-upload-btn">
                Upload &amp; Transcribe
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadBlob(recoveredBlob)} className="rounded-full text-xs">Download</Button>
              <Button size="sm" variant="ghost" onClick={() => { clearSession(sessionId); setRecoveredBlob(null); }} className="rounded-full text-xs text-slate-400" data-testid="recover-discard-btn">Discard</Button>
            </div>
          </div>
        )}
        <Button onClick={startRecording} variant="outline" className="rounded-full gap-2 border-red-200 text-red-600 hover:bg-red-50" data-testid="start-session-recording">
          <Mic className="w-4 h-4" /> Start Recording
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 p-3 rounded-xl bg-red-50/50 border border-red-200" data-testid="session-recording-controls">
        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="font-mono text-sm font-medium">{formatTime(elapsed)}</span>
        {wsConnected && (
          <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-600 gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Live
          </Badge>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          {paused ? (
            <Button onClick={resumeRecording} size="sm" variant="outline" className="rounded-full gap-1 h-7 text-xs"><Play className="w-3 h-3" /> Resume</Button>
          ) : (
            <Button onClick={pauseRecording} size="sm" variant="outline" className="rounded-full gap-1 h-7 text-xs"><Pause className="w-3 h-3" /> Pause</Button>
          )}
          <Button onClick={stopRecording} size="sm" className="rounded-full gap-1 h-7 text-xs bg-red-600 hover:bg-red-700" data-testid="stop-session-recording">
            <Square className="w-3 h-3" /> Stop
          </Button>
        </div>
      </div>
      {(liveTranscript || interimText) ? (
        <div ref={liveBoxRef} className="p-3 rounded-lg bg-white border border-emerald-200 max-h-44 overflow-y-auto" data-testid="live-transcript">
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-1">Live Transcript</p>
          <p className="text-sm text-slate-700 leading-relaxed">{liveTranscript}{interimText && <span className="text-slate-400 italic"> {interimText}</span>}</p>
        </div>
      ) : (
        <div className="p-3 rounded-lg bg-white border border-emerald-200" data-testid="live-transcript-waiting">
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-1 flex items-center gap-1.5">
            Live Transcript
            <span className="inline-flex items-center gap-1 font-normal normal-case tracking-normal text-emerald-500">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" /> Listening…
            </span>
          </p>
          <p className="text-xs text-slate-400 italic">
            Speech will appear here as it's picked up. Live view works word-by-word for English &amp; Hindi — Punjabi and other languages are fully transcribed right after you press Stop.
          </p>
        </div>
      )}
    </div>
  );
}

function SessionCard({ session, cardId, onRefresh }) {
  const [processing, setProcessing] = useState(
    session.processing_status === 'transcribing' || session.processing_status === 'summarizing'
  );
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);

  const startPolling = () => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/consult-cards/${cardId}/sessions/${session.id}/status`, { withCredentials: true });
        if (data.processing_status === 'completed' || data.processing_status === 'failed') {
          clearInterval(pollRef.current);
          setProcessing(false);
          onRefresh?.();
        }
      } catch { clearInterval(pollRef.current); setProcessing(false); }
    }, 3000);
  };

  useEffect(() => {
    if (session.processing_status === 'transcribing' || session.processing_status === 'summarizing') {
      setProcessing(true);
      startPolling();
    }
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.processing_status]);

  const handleProcess = async () => {
    setProcessing(true);
    try { await axios.post(`${API_URL}/api/consult-cards/${cardId}/sessions/${session.id}/process`, {}, { withCredentials: true }); startPolling(); }
    catch { setProcessing(false); toast.error('Failed'); }
  };

  const handleSummarize = async () => {
    setProcessing(true);
    try { await axios.post(`${API_URL}/api/consult-cards/${cardId}/sessions/${session.id}/summarize`, {}, { withCredentials: true }); startPolling(); }
    catch { setProcessing(false); toast.error('Failed'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/consult-cards/${cardId}/sessions/${session.id}`, { withCredentials: true });
      toast.success('Session deleted');
      onRefresh?.();
    } catch { toast.error('Delete failed'); setDeleting(false); }
  };

  const isHistory = session.session_type === 'history';
  const Icon = isHistory ? ClipboardList : Stethoscope;
  const label = isHistory ? 'History' : 'Doctor Consult';

  return (
    <Card className={`${isHistory ? 'border-amber-200 bg-amber-50/30' : 'border-violet-200 bg-violet-50/30'} shadow-sm`} data-testid={`session-card-${session.id}`}>
      <CardHeader className="pb-1 pt-2.5 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`w-3.5 h-3.5 ${isHistory ? 'text-amber-600' : 'text-violet-600'}`} />
            <span className="text-xs font-medium">{label}</span>
            <span className="text-[10px] text-muted-foreground">{formatTimeIST(session.created_at)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className={`text-[10px] ${
              session.processing_status === 'completed' ? 'border-emerald-200 text-emerald-600' :
              session.processing_status === 'failed' ? 'border-red-200 text-red-600' :
              processing ? 'border-[rgba(14,81,50,0.15)] text-[#0E5132]' : 'border-slate-200 text-slate-400'
            }`}>
              {processing && <Loader2 className="w-2.5 h-2.5 animate-spin mr-1" />}
              {session.processing_status === 'completed' ? 'Done' : session.processing_status === 'failed' ? 'Failed' :
               session.processing_status === 'transcript_ready' ? 'Ready' : processing ? 'Processing...' :
               session.audio_url ? 'Audio Ready' : 'Waiting'}
            </Badge>
            <Button onClick={handleDelete} disabled={deleting} variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-session-${session.id}`}>
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2">
        {!session.audio_url && !session.transcript && (
          <SessionRecorder cardId={cardId} sessionId={session.id} onComplete={onRefresh} />
        )}

        {session.audio_url && (
          <AudioPlayer src={session.audio_url} label="Session Recording" />
        )}

        {session.audio_url && !session.processing_status && !session.transcript && (
          <Button onClick={handleProcess} disabled={processing} variant="outline" size="sm" className="rounded-full gap-1.5 text-xs">
            <Sparkles className="w-3 h-3" /> Transcribe & Summarize
          </Button>
        )}

        {session.audio_url && session.processing_status === 'failed' && (
          <Button onClick={handleProcess} disabled={processing} variant="outline" size="sm" className="rounded-full gap-1.5 text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`retry-session-${session.id}`}>
            {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Retry Transcription
          </Button>
        )}

        {session.processing_status === 'transcript_ready' && !session.summary && (
          <Button onClick={handleSummarize} disabled={processing} variant="outline" size="sm" className="rounded-full gap-1.5 text-xs border-emerald-200 text-emerald-600">
            <Sparkles className="w-3 h-3" /> Generate Summary
          </Button>
        )}

        {session.transcript && (
          <details className="group">
            <summary className="text-[10px] font-bold uppercase tracking-wider text-slate-500 cursor-pointer select-none">
              Transcript <span className="text-slate-400 font-normal">(click to expand)</span>
            </summary>
            <div className="p-2 mt-1 rounded-lg bg-white border border-slate-100 max-h-28 overflow-y-auto">
              <p className="text-[11px] text-slate-600 whitespace-pre-wrap">{session.transcript}</p>
            </div>
          </details>
        )}

        {session.summary && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Summary</p>
            <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-100 max-h-28 overflow-y-auto">
              <p className="text-[11px] text-emerald-800 whitespace-pre-wrap">{session.summary}</p>
            </div>
          </div>
        )}

        {session.processing_error && (
          <div className="flex items-center gap-1 text-[11px] text-red-600">
            <AlertTriangle className="w-3 h-3" />
            {session.processing_error.includes('No such file') || session.processing_error.includes('Errno')
              ? 'Audio file not found. The recording was lost after a server update. Please delete this session and re-record.'
              : session.processing_error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Stock / discontinued badges for a pharmacy medicine
function MedicineStockBadge({ med, inline = false }) {
  if (!med) return null;
  const outOfStock = med.in_stock === false || (med.stock_status && med.stock_status !== 'available');
  const archived = med.is_archived === true;
  if (!outOfStock && !archived) return null;
  const base = inline ? 'inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-1.5 py-0.5' : 'inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-semibold ml-1';
  return (
    <span className="inline-flex items-center gap-1">
      {outOfStock && (
        <span className={`${base} bg-red-50 text-red-600 border border-red-200`} data-testid="medicine-out-of-stock-badge">
          {inline && <AlertTriangle className="w-3 h-3" />}Out of stock{typeof med.stock_qty === 'number' ? ` (${med.stock_qty})` : ''}
        </span>
      )}
      {archived && (
        <span className={`${base} bg-amber-50 text-amber-700 border border-amber-200`} title={med.archive_reason || 'Archived by pharmacy — no new stock will be purchased'} data-testid="medicine-discontinued-badge">
          {inline && <AlertTriangle className="w-3 h-3" />}Discontinued{inline && typeof med.stock_qty === 'number' ? ` · ${med.stock_qty} left` : ''}
        </span>
      )}
    </span>
  );
}

// Medicine search with dropdown
function MedicineSearchInput({ value, onChange, medicines }) {
  const [query, setQuery] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    if (query.length >= 1 && medicines.length > 0) {
      const q = query.toLowerCase();
      setFiltered(medicines.filter(m =>
        m.name.toLowerCase().includes(q) || m.generic_name?.toLowerCase().includes(q) || m.brand?.toLowerCase().includes(q)
      ).slice(0, 8));
    } else {
      setFiltered(medicines.slice(0, 8));
    }
  }, [query, medicines]);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setShowDropdown(true); }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        className="h-8 text-xs rounded-md px-2"
        placeholder="Type medicine name..."
        data-testid="medicine-search-input"
      />
      {showDropdown && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="medicine-dropdown">
          {filtered.map(med => (
            <button
              key={med.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-[#E6EFEA] text-xs border-b border-slate-50 last:border-0"
              onMouseDown={(e) => {
                e.preventDefault();
                setQuery(med.name);
                onChange(med.name, med.id);
                setShowDropdown(false);
              }}
              data-testid={`medicine-option-${med.id}`}
            >
              <p className="font-medium text-slate-800">{med.name}<MedicineStockBadge med={med} /></p>
              <p className="text-[10px] text-slate-500">{[med.generic_name, med.brand, med.form].filter(Boolean).join(' · ')}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VoiceReasonButton({ onTranscript, onAudioUrl }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 48000 });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) { toast.error('Recording too short'); return; }
        setTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'voice_reason.webm');
          const { data } = await axios.post(`${API_URL}/api/consult-cards/voice-transcribe`, formData, { withCredentials: true });
          if (data.text) {
            onTranscript(data.text);
            toast.success('Voice transcribed');
          } else {
            toast.error(data.error || 'Could not transcribe');
          }
          if (data.audio_url) onAudioUrl?.(data.audio_url);
        } catch { toast.error('Transcription failed'); }
        finally { setTranscribing(false); }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch { /* ok */ }
    setRecording(false);
  };

  return (
    <div className="absolute right-1.5 top-1.5">
      {transcribing ? (
        <div className="h-7 w-7 flex items-center justify-center"><Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" /></div>
      ) : recording ? (
        <Button type="button" variant="ghost" size="icon" onClick={stopRecording}
          className="h-7 w-7 bg-red-50 text-red-600 hover:bg-red-100 rounded-full animate-pulse" data-testid="stop-voice-reason">
          <Square className="w-3 h-3" />
        </Button>
      ) : (
        <Button type="button" variant="ghost" size="icon" onClick={startRecording}
          className="h-7 w-7 text-blue-500 hover:bg-[#E6EFEA] rounded-full" title="Speak your reason" data-testid="start-voice-reason">
          <Mic className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
}

function PrescriptionViewModal({ open, onClose, prescription, visitDate, canEdit, onEdit, patientName, patientPhone, patientAge, patientGender, doctorName }) {
  if (!prescription || !open) return null;

  const allMeds = [];
  let counter = 0;
  for (const section of ['morning_routine', 'evening_routine', 'oral_medicines']) {
    const meds = prescription[section]?.medicines || [];
    const sectionLabel = section === 'morning_routine' ? 'Morning' : section === 'evening_routine' ? 'Evening' : 'Oral';
    meds.forEach(med => {
      counter++;
      allMeds.push({ ...med, _num: counter, _section: sectionLabel });
    });
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0" data-testid="prescription-view-modal">
        {/* Clinic Header */}
        <div className="bg-[#0E5132] text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold tracking-wide">{doctorName || 'Doctor'}</p>
              <p className="text-[11px] text-emerald-200">Eden Skin Clinic</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-emerald-200">PRESCRIPTION</p>
              <p className="text-xs font-medium">{visitDate ? formatDateIST(visitDate) : ''}</p>
            </div>
          </div>
        </div>

        {/* Patient Info Bar */}
        <div className="px-6 py-2.5 bg-[#E6EFEA] border-b border-emerald-200">
          <p className="text-xs font-bold text-[#0E5132]">
            {patientName || 'Patient'}{patientAge ? ` (${patientAge}y` : ''}{patientGender && patientGender !== 'other' ? `, ${patientGender === 'male' ? 'M' : 'F'})` : patientAge ? ')' : ''}
            {patientPhone ? ` - +91${patientPhone}` : ''}
          </p>
        </div>

        {/* Treatment Table */}
        <div className="px-6 py-4 space-y-4">
          {allMeds.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#0E5132] mb-2">Treatment</p>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-[#0E5132]/20">
                    <th className="text-left py-1.5 pr-2 text-[9px] uppercase tracking-wider text-slate-500 w-6">#</th>
                    <th className="text-left py-1.5 pr-2 text-[9px] uppercase tracking-wider text-slate-500">Medicine</th>
                    <th className="text-left py-1.5 pr-2 text-[9px] uppercase tracking-wider text-slate-500 w-16">Frequency</th>
                    <th className="text-left py-1.5 text-[9px] uppercase tracking-wider text-slate-500">Instructions / Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {allMeds.map((med) => (
                    <tr key={med._num} className="border-b border-slate-100">
                      <td className="py-2 pr-2 text-[11px] text-slate-400 align-top">{med._num}</td>
                      <td className="py-2 pr-2 align-top">
                        <p className="text-[11px] font-semibold text-slate-800">{med.medicine_name}</p>
                        {med.dosage && <p className="text-[10px] text-slate-500">{med.dosage}</p>}
                        <Badge variant="outline" className="text-[8px] mt-0.5 border-emerald-200 text-emerald-600">{med._section}</Badge>
                      </td>
                      <td className="py-2 pr-2 text-[11px] text-slate-600 align-top">{med.frequency || '-'}</td>
                      <td className="py-2 text-[11px] text-slate-600 align-top">
                        {med.instructions && <span>{med.instructions}</span>}
                        {med.duration && <span className="text-slate-400">{med.instructions ? ' — ' : ''}{med.duration}</span>}
                        {!med.instructions && !med.duration && '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Precautions */}
          {prescription.precautions && prescription.precautions !== 'None' && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#0E5132] mb-1">Precautions</p>
              <p className="text-[11px] text-slate-700 whitespace-pre-wrap">{prescription.precautions}</p>
            </div>
          )}

          {/* Follow-up */}
          {prescription.follow_up_date_suggestion && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#0E5132] mb-1">Follow-up</p>
              <p className="text-[11px] text-slate-700">{prescription.follow_up_date_suggestion ? formatFollowUpDate(prescription.follow_up_date_suggestion) : ''}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <p className="text-[9px] text-slate-400">Eden Skin Clinic</p>
          <div className="flex items-center gap-2">
            {!canEdit && (
              <span className="text-[9px] text-amber-600 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Locked</span>
            )}
            <Button variant="outline" size="sm" onClick={onClose} className="rounded-full text-[10px] h-7">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SuggestedQuestionsPanel({ cardId, sessions }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const [fetched, setFetched] = useState(false);

  // Only fetch when there are completed sessions with summaries
  const hasSummaries = sessions?.some(s => s.summary && s.processing_status === 'completed');

  const fetchQuestions = useCallback(async () => {
    if (!hasSummaries || fetched) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/consult-cards/${cardId}/suggested-questions`, { withCredentials: true });
      setQuestions(data.questions || []);
      setFetched(true);
    } catch (err) {
      console.error('Failed to fetch suggested questions', err);
    } finally {
      setLoading(false);
    }
  }, [cardId, hasSummaries, fetched]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  // Re-fetch when sessions change (new summary added)
  useEffect(() => { setFetched(false); }, [sessions?.length, sessions?.filter(s => s.summary).length]);

  const dismiss = (idx) => setDismissed(prev => new Set([...prev, idx]));
  const visibleQuestions = questions.filter((_, i) => !dismissed.has(i));

  if (!hasSummaries || (visibleQuestions.length === 0 && !loading)) return null;

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3 space-y-2" data-testid="suggested-questions-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-semibold text-sky-700 flex items-center gap-1.5">
          <HelpCircle className="w-3.5 h-3.5" />
          Suggested Questions
        </h3>
        {!loading && visibleQuestions.length > 0 && (
          <span className="text-[9px] text-sky-500">{visibleQuestions.length} remaining</span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-2">
          <Loader2 className="w-3 h-3 animate-spin text-sky-500" />
          <span className="text-[10px] text-sky-600">Analyzing consultation...</span>
        </div>
      ) : (
        <div className="space-y-1.5">
          {visibleQuestions.map((q, i) => {
            const originalIdx = questions.indexOf(q);
            return (
              <div key={originalIdx} className="flex items-start gap-2 group" data-testid={`suggested-q-${originalIdx}`}>
                <div className="flex-1 p-2 rounded-md bg-white border border-sky-100">
                  <p className="text-[11px] text-slate-700 font-medium">{q.question}</p>
                  {q.reason && <p className="text-[9px] text-slate-400 mt-0.5">{q.reason}</p>}
                </div>
                <button
                  onClick={() => dismiss(originalIdx)}
                  className="mt-1 p-1 rounded text-sky-300 hover:text-sky-600 hover:bg-sky-100 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Dismiss"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function DoctorsBriefPanel({ cardId, historySessions }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const completedCount = historySessions?.filter(s => s.summary && s.processing_status === 'completed').length || 0;

  const fetchBrief = useCallback(async () => {
    if (completedCount < 1) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/consult-cards/${cardId}/doctors-brief`, { withCredentials: true });
      if (data.brief) setBrief(data.brief);
    } catch (err) {
      console.error('Failed to fetch doctor brief', err);
    } finally {
      setLoading(false);
    }
  }, [cardId, completedCount]);

  useEffect(() => { fetchBrief(); }, [fetchBrief]);

  if (completedCount < 1 && !brief) return null;

  return (
    <Card className="border-indigo-200 bg-indigo-50/30 shadow-sm" data-testid="doctors-brief-panel">
      <CardHeader className="pb-1 pt-3 px-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-heading flex items-center gap-2 text-indigo-700">
            <BrainCircuit className="w-4 h-4 text-indigo-500" />
            Doctor&apos;s Brief
            <Badge variant="outline" className="text-[9px] bg-indigo-100 text-indigo-600 border-indigo-200">
              {completedCount} session{completedCount !== 1 ? 's' : ''}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {!loading && brief && (
              <Button
                variant="ghost" size="sm"
                className="h-6 px-2 text-[10px] text-indigo-500 hover:text-indigo-700 hover:bg-indigo-100"
                onClick={(e) => { e.stopPropagation(); setBrief(null); fetchBrief(); }}
                data-testid="refresh-brief-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            )}
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-indigo-400" /> : <ChevronDown className="w-3.5 h-3.5 text-indigo-400" />}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="px-4 pb-3">
          {loading ? (
            <div className="flex items-center gap-2 py-3">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
              <span className="text-[11px] text-indigo-600">Preparing case summary...</span>
            </div>
          ) : brief ? (
            <div className="prose prose-xs max-w-none text-[11px] text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="doctors-brief-content">
              {brief}
            </div>
          ) : (
            <p className="text-[10px] text-muted-foreground py-2">Complete history sessions to generate the brief.</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}



function EdenVisitCard({ visit }) {
  const statusColors = {
    checked_in: 'bg-[#E6EFEA] text-[#0E5132]',
    checked_out: 'bg-slate-100 text-slate-600',
    scheduled: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-violet-100 text-violet-700',
    forwarded: 'bg-slate-100 text-slate-500',
  };
  return (
    <div className="border border-emerald-100 rounded-lg p-3 bg-emerald-50/30" data-testid={`eden-visit-${visit.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3 text-emerald-500" />
          <span className="text-[11px] font-medium text-slate-700">
            {visit.scheduled_date || 'Unknown date'}
          </span>
          {visit.scheduled_time && (
            <span className="text-[10px] text-muted-foreground">{visit.scheduled_time}</span>
          )}
          <Badge className="h-4 px-1.5 text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 gap-0.5">Eden</Badge>
          <Badge variant="outline" className={`text-[9px] ${statusColors[visit.status] || 'bg-slate-100 text-slate-500'}`}>
            {visit.status?.replace('_', ' ')}
          </Badge>
        </div>
        {visit.ref_number && (
          <span className="text-[9px] text-muted-foreground font-mono">{visit.ref_number}</span>
        )}
      </div>
      <div className="pl-5 space-y-1">
        <p className="text-[11px] text-slate-700 font-medium">{visit.service_name}</p>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          {visit.service_type && <span>{visit.service_type === 'queue_based' ? 'Queue' : 'Appointment'}</span>}
          {visit.token_number && <span>Token #{visit.token_number}</span>}
          {visit.is_paid && <span className="text-emerald-600">Paid{visit.amount_paid > 0 ? ` (₹${visit.amount_paid})` : ''}</span>}
        </div>
        {visit.remarks && <p className="text-[10px] text-slate-500 italic">{visit.remarks}</p>}
      </div>
    </div>
  );
}

function PreviousVisitsPanel({ previousVisits, edenVisits, patientName, patientPhone, patientAge, patientGender }) {
  const [expanded, setExpanded] = useState(true);
  const [viewPrescription, setViewPrescription] = useState(null);

  const hasLocal = previousVisits && previousVisits.length > 0;
  const hasEden = edenVisits && edenVisits.length > 0;
  if (!hasLocal && !hasEden) return null;

  const totalCount = (previousVisits?.length || 0) + (edenVisits?.length || 0);

  return (
    <>
      <Card className="border-slate-200 shadow-sm mt-4" data-testid="previous-visits-panel">
        <CardHeader className="pb-1 pt-3 px-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-heading flex items-center gap-2 text-slate-700">
              <History className="w-4 h-4 text-slate-500" />
              Previous Visits
              <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500">
                {totalCount} visit{totalCount !== 1 ? 's' : ''}
              </Badge>
              {hasEden && (
                <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 gap-0.5 h-4 px-1.5">
                  {edenVisits.length} Eden
                </Badge>
              )}
            </CardTitle>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </CardHeader>
        {expanded && (
          <CardContent className="px-4 pb-3 space-y-3">
            {/* Local clinic visits */}
            {hasLocal && previousVisits.map((visit) => (
              <div key={visit.id} className="border border-slate-100 rounded-lg p-3 bg-slate-50/50" data-testid={`prev-visit-${visit.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 text-slate-400" />
                    <span className="text-[11px] font-medium text-slate-700">
                      {visit.created_at ? formatDateIST(visit.created_at) : 'Unknown'}
                    </span>
                    <Badge variant="outline" className={`text-[9px] ${STAGE_CONFIG[visit.stage]?.color || 'bg-slate-100 text-slate-500'}`}>
                      {STAGE_CONFIG[visit.stage]?.label || visit.stage}
                    </Badge>
                    {!visit.can_edit_prescription && (
                      <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-400 gap-0.5">
                        <Lock className="w-2 h-2" /> Locked
                      </Badge>
                    )}
                  </div>
                  {visit.ai_prescription_draft && !visit.ai_prescription_draft.parse_error && !visit.ai_prescription_draft.insufficient_data && (
                    <Button variant="outline" size="sm" className="rounded-full gap-1 text-[10px] h-6"
                      onClick={() => setViewPrescription(visit)} data-testid={`view-prev-rx-${visit.id}`}>
                      <Eye className="w-3 h-3" /> View Prescription
                    </Button>
                  )}
                </div>

                {/* Session summaries */}
                {visit.summaries?.length > 0 && (
                  <div className="space-y-1.5">
                    {visit.summaries.map((s, i) => (
                      <div key={i} className="pl-3 border-l-2 border-slate-200">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          {s.type === 'history' ? <ClipboardList className="w-2.5 h-2.5 text-amber-500" /> : <Stethoscope className="w-2.5 h-2.5 text-blue-500" />}
                          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                            {s.type === 'history' ? 'History' : 'Doctor Consult'}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-600 whitespace-pre-wrap line-clamp-4">{s.summary}</p>
                      </div>
                    ))}
                  </div>
                )}

                {!visit.summaries?.length && !visit.ai_prescription_draft && (
                  <p className="text-[10px] text-muted-foreground italic">No summary or prescription recorded</p>
                )}
              </div>
            ))}
            {/* Eden appointment history */}
            {hasEden && (
              <>
                {hasLocal && (
                  <div className="flex items-center gap-2 pt-1">
                    <div className="flex-1 h-px bg-emerald-200" />
                    <span className="text-[9px] font-medium text-emerald-600 uppercase tracking-wider">Eden History</span>
                    <div className="flex-1 h-px bg-emerald-200" />
                  </div>
                )}
                {edenVisits.map((visit) => (
                  <EdenVisitCard key={visit.id} visit={visit} />
                ))}
              </>
            )}
          </CardContent>
        )}
      </Card>

      <PrescriptionViewModal
        open={!!viewPrescription}
        onClose={() => setViewPrescription(null)}
        prescription={viewPrescription?.ai_prescription_draft}
        visitDate={viewPrescription?.created_at}
        canEdit={viewPrescription?.can_edit_prescription}
        patientName={patientName}
        patientPhone={patientPhone}
        patientAge={patientAge}
        patientGender={patientGender}
        doctorName={viewPrescription?.assigned_doctor_name}
      />
    </>
  );
}

function PrescriptionEditModal({ open, onClose, draft, onSave, medicines }) {
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reasonForChange, setReasonForChange] = useState('');
  const [draftProtocolCreated, setDraftProtocolCreated] = useState(null);
  // Voice dictation state
  const [dictating, setDictating] = useState(false);
  const [dictateProcessing, setDictateProcessing] = useState(false);
  const [dictateTranscript, setDictateTranscript] = useState('');
  const [dictateAudioUrl, setDictateAudioUrl] = useState(null);
  const [reasonAudioUrl, setReasonAudioUrl] = useState(null);
  const dictateRecorderRef = useRef(null);
  const dictateChunksRef = useRef([]);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (open && draft && !initializedRef.current) {
      setEditData(JSON.parse(JSON.stringify(draft)));
      setReasonForChange('');
      setDraftProtocolCreated(null);
      setDictateTranscript('');
      setDictateAudioUrl(null);
      setReasonAudioUrl(null);
      initializedRef.current = true;
    }
    if (!open) {
      initializedRef.current = false;
    }
  }, [open, draft]);

  if (!editData) return null;

  const updateMed = (section, idx, field, value, medId) => {
    const updated = { ...editData };
    const meds = [...(updated[section]?.medicines || [])];
    meds[idx] = { ...meds[idx], [field]: value };
    if (medId) meds[idx].medicine_id = medId;
    updated[section] = { ...updated[section], medicines: meds };
    setEditData(updated);
  };

  const removeMed = (section, idx) => {
    const updated = { ...editData };
    const meds = (updated[section]?.medicines || []).filter((_, i) => i !== idx);
    updated[section] = { ...updated[section], medicines: meds };
    setEditData(updated);
  };

  const addMed = (section) => {
    const updated = { ...editData };
    const meds = [...(updated[section]?.medicines || []), { medicine_name: '', frequency: '', duration: '', instructions: '' }];
    updated[section] = { ...updated[section], medicines: meds };
    setEditData(updated);
  };

  // Voice dictation: record → combined transcribe+AI edit in ONE call
  const startDictation = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 32000 });
      dictateChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) dictateChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(dictateChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) { toast.error('Recording too short'); return; }
        setDictateProcessing(true);
        try {
          // Combined: transcribe + AI edit in one round trip
          const cleanPrescription = {
            morning_routine: editData.morning_routine || { medicines: [], notes: '' },
            evening_routine: editData.evening_routine || { medicines: [], notes: '' },
            oral_medicines: editData.oral_medicines || { medicines: [], notes: '' },
            precautions: editData.precautions || '',
            follow_up_date_suggestion: editData.follow_up_date_suggestion || '',
          };
          const formData = new FormData();
          formData.append('file', blob, 'dictate_edit.webm');
          formData.append('current_prescription', JSON.stringify(cleanPrescription));
          const { data: result } = await axios.post(
            `${API_URL}/api/consult-cards/voice-dictate-and-edit`,
            formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
          );
          if (result.text) setDictateTranscript(result.text);
          if (result.audio_url) setDictateAudioUrl(result.audio_url);
          if (result.timings) console.log('[Voice perf]', result.timings);
          if (result.updated_prescription) {
            setEditData(prev => ({
              ...prev,
              morning_routine: result.updated_prescription.morning_routine || prev.morning_routine,
              evening_routine: result.updated_prescription.evening_routine || prev.evening_routine,
              oral_medicines: result.updated_prescription.oral_medicines || prev.oral_medicines,
              precautions: result.updated_prescription.precautions ?? prev.precautions,
              follow_up_date_suggestion: result.updated_prescription.follow_up_date_suggestion ?? prev.follow_up_date_suggestion,
            }));
            setReasonForChange(prev => {
              const dictNote = `[Voice edit: "${result.text}"]`;
              return prev ? prev + ' ' + dictNote : dictNote;
            });
            toast.success('Changes applied from voice');
          } else {
            toast.error(result.error || 'Could not apply changes');
          }
        } catch { toast.error('Voice edit failed'); }
        finally { setDictateProcessing(false); }
      };
      recorder.start();
      dictateRecorderRef.current = recorder;
      setDictating(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopDictation = () => {
    try { dictateRecorderRef.current?.stop(); } catch { /* ok */ }
    setDictating(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...editData, _reason_for_change: reasonForChange };
    const result = await onSave(payload);
    if (result?.draft_protocol_created) {
      setDraftProtocolCreated(result.draft_protocol_created);
    } else {
      onClose();
    }
    setSaving(false);
  };

  const renderSection = (key, title) => {
    const meds = editData[key]?.medicines || [];
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-slate-700">{title}</h3>
          <Button variant="outline" size="sm" onClick={() => addMed(key)} className="rounded-full gap-1 h-7 text-xs">
            <Plus className="w-3 h-3" /> Add Medicine
          </Button>
        </div>
        {meds.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No medicines in this section</p>
        ) : (
          <div className="space-y-2">
            {meds.map((med, i) => (
              <div key={i} className="flex gap-2 items-start p-2 rounded-lg bg-slate-50 border border-slate-100 group">
                <span className="text-xs text-slate-400 mt-2 w-4">{i + 1}.</span>
                <div className="flex-1 space-y-1.5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="sm:col-span-1">
                      <label className="text-[9px] uppercase tracking-wider text-slate-400">Medicine</label>
                      <MedicineSearchInput
                        value={med.medicine_name}
                        onChange={(val, id) => updateMed(key, i, 'medicine_name', val, id)}
                        medicines={medicines}
                      />
                      {(() => {
                        const q = (med.medicine_name || '').trim().toLowerCase();
                        const info = q ? medicines.find(m => m.name?.toLowerCase() === q) : null;
                        return info ? <div className="mt-1" data-testid={`medicine-row-stock-warning-${key}-${i}`}><MedicineStockBadge med={info} inline /></div> : null;
                      })()}
                    </div>
                    <div>
                      <label className="text-[9px] uppercase tracking-wider text-slate-400">Frequency <span className="normal-case text-slate-300">(e.g. 1-0-1)</span></label>
                      <Input value={med.frequency || ''} onChange={e => updateMed(key, i, 'frequency', e.target.value)} placeholder="1-0-1" className="h-8 text-xs rounded-md px-2" />
                    </div>
                    <div>
                      <label className="text-[9px] uppercase tracking-wider text-slate-400">Duration</label>
                      <Input value={med.duration || ''} onChange={e => updateMed(key, i, 'duration', e.target.value)} placeholder="2 months" className="h-8 text-xs rounded-md px-2" />
                    </div>
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="text-[9px] uppercase tracking-wider text-slate-400">Instructions</label>
                      <Input value={med.instructions || ''} onChange={e => updateMed(key, i, 'instructions', e.target.value)} placeholder="Take 1 tablet after meals" className="h-8 text-xs rounded-md px-2" />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeMed(key, i)} className="h-8 w-8 text-red-400 hover:text-red-600 shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Draft protocol created notification
  if (draftProtocolCreated) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="draft-protocol-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-[#0E5132]">
              <BookOpen className="w-4 h-4" /> Draft Protocol Created
            </DialogTitle>
            <DialogDescription>A new draft protocol was auto-generated from this prescription.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="p-3 rounded-lg bg-[#E6EFEA] border border-[rgba(14,81,50,0.15)] space-y-1.5">
              <p className="text-xs font-medium text-blue-800">Condition: {draftProtocolCreated.condition}</p>
              <p className="text-[10px] text-[#0E5132]">Status: <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">Draft — Needs Review</Badge></p>
              {draftProtocolCreated.recommended_treatments?.length > 0 && (
                <div>
                  <p className="text-[10px] text-blue-500 font-medium">Treatments extracted:</p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {draftProtocolCreated.recommended_treatments.map(t => (
                      <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-500">Ask an admin to review and complete this protocol in the Knowledge Base. The AI agent will use it for future prescriptions of similar conditions.</p>
          </div>
          <DialogFooter>
            <Button onClick={onClose} className="rounded-full bg-[#0E5132] hover:bg-[#063D2F]" data-testid="close-draft-protocol-dialog">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="prescription-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Pencil className="w-4 h-4" /> Edit Prescription
          </DialogTitle>
          <DialogDescription>Modify medicines, frequency, and instructions. Medicine names suggest from clinic inventory.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Voice Dictation Section */}
          <div className="p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-[rgba(14,81,50,0.15)]" data-testid="dictate-changes-section">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-[#0E5132] flex items-center gap-1.5">
                <Mic className="w-3.5 h-3.5" />
                Dictate Changes
                <span className="text-blue-400 font-normal text-[10px]">— speak your modifications</span>
              </label>
              {dictateProcessing && <Badge variant="outline" className="text-[9px] bg-[#E6EFEA] text-[#0E5132] animate-pulse gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" /> AI applying changes...</Badge>}
            </div>
            <div className="flex items-center gap-2">
              {dictating ? (
                <Button onClick={stopDictation} variant="destructive" size="sm" className="rounded-full gap-1.5 text-xs h-8 animate-pulse" data-testid="stop-dictation-btn">
                  <Square className="w-3 h-3" /> Stop & Apply
                </Button>
              ) : (
                <Button onClick={startDictation} disabled={dictateProcessing} variant="outline" size="sm" className="rounded-full gap-1.5 text-xs h-8 border-blue-300 text-[#0E5132] hover:bg-[#E6EFEA]" data-testid="start-dictation-btn">
                  {dictateProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />}
                  {dictateProcessing ? 'Processing...' : 'Start Dictation'}
                </Button>
              )}
              {dictating && <span className="text-[10px] text-red-600 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Listening...</span>}
            </div>
            {dictateTranscript && (
              <div className="mt-2 p-2 rounded-md bg-white/80 border border-blue-100 space-y-1.5">
                <p className="text-[9px] font-bold uppercase tracking-wider text-blue-500 mb-0.5">Your instruction:</p>
                <p className="text-[10px] text-slate-700 italic">&ldquo;{dictateTranscript}&rdquo;</p>
                {dictateAudioUrl && <AudioPlayer src={dictateAudioUrl} />}
              </div>
            )}
            <p className="text-[9px] text-blue-400 mt-1.5">Say something like: &ldquo;Change adapalene to twice daily&rdquo; or &ldquo;Add vitamin C serum to morning routine&rdquo; or &ldquo;Remove oral antibiotics&rdquo;</p>
          </div>

          <div className="flex items-center gap-2 text-[10px] text-slate-400">
            <div className="flex-1 h-px bg-slate-200" />
            <span>or edit manually below</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {renderSection('morning_routine', 'Morning Routine')}
          {renderSection('evening_routine', 'Evening Routine')}
          {renderSection('oral_medicines', 'Oral Medicines')}

          {/* Provisional Diagnosis */}
          <div className="space-y-2 border border-blue-200 rounded-lg p-3 bg-blue-50/30">
            <label className="text-xs font-medium text-blue-700">Provisional Diagnosis</label>
            <div className="flex flex-wrap gap-1.5 min-h-[28px]">
              {(editData.probable_diagnosis?.tags || []).map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-[11px] px-2 py-0.5 rounded-full">
                  {tag}
                  <button type="button" onClick={() => {
                    const tags = [...(editData.probable_diagnosis?.tags || [])];
                    tags.splice(i, 1);
                    setEditData(prev => ({ ...prev, probable_diagnosis: { ...prev.probable_diagnosis, tags } }));
                  }} className="text-blue-400 hover:text-red-500">×</button>
                </span>
              ))}
              <input
                placeholder="Type diagnosis & press Enter"
                className="text-xs bg-transparent border-none outline-none flex-1 min-w-[140px] h-7 placeholder:text-blue-300"
                data-testid="diagnosis-tag-input"
                onKeyDown={e => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    e.preventDefault();
                    const tags = [...(editData.probable_diagnosis?.tags || []), e.target.value.trim()];
                    setEditData(prev => ({ ...prev, probable_diagnosis: { ...prev.probable_diagnosis, tags } }));
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <Textarea
              value={editData.probable_diagnosis?.notes || ''}
              onChange={e => setEditData(prev => ({ ...prev, probable_diagnosis: { ...prev.probable_diagnosis, notes: e.target.value } }))}
              placeholder="Additional diagnosis notes (optional)"
              className="text-xs min-h-[40px] bg-white"
              data-testid="diagnosis-notes-input"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Procedure Suggested</label>
            <ProcedurePicker onPick={(name) => setEditData(prev => ({ ...prev, procedures: prev.procedures && prev.procedures !== 'None' ? `${prev.procedures}, ${name}` : name }))} />
            <Textarea
              value={editData.procedures === 'None' ? '' : (editData.procedures || '')}
              onChange={e => setEditData(prev => ({ ...prev, procedures: e.target.value }))}
              placeholder="Procedures advised — AI suggests from the procedure library; pick more from the dropdown above"
              className="mt-1 text-xs min-h-[50px]"
              data-testid="procedures-input"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Precautions</label>
            <Textarea value={editData.precautions || ''} onChange={e => setEditData(prev => ({ ...prev, precautions: e.target.value }))} className="mt-1 text-xs min-h-[60px]" />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Follow-up Date</label>
            <Input type="date" value={editData.follow_up_date_suggestion || ''} onChange={e => setEditData(prev => ({ ...prev, follow_up_date_suggestion: e.target.value }))} className="mt-1 text-xs" data-testid="follow-up-date-input" />
          </div>

          {/* Reason for Change with Voice Input */}
          <div className="border-t border-slate-200 pt-3" data-testid="reason-for-change-section">
            <label className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-[#0E5132]" />
              Reason for Changes <span className="text-slate-400 font-normal">(helps the AI learn your preferences)</span>
            </label>
            <div className="relative mt-1.5">
              <Textarea
                value={reasonForChange}
                onChange={e => setReasonForChange(e.target.value)}
                placeholder="Speak or type your reason... e.g., Patient prefers cream over gel, reduced dosage due to sensitivity"
                className="text-xs min-h-[60px] border-[rgba(14,81,50,0.15)] focus:border-blue-400 pr-12"
                data-testid="reason-for-change-input"
              />
              <VoiceReasonButton
                onTranscript={text => setReasonForChange(prev => prev ? prev + ' ' + text : text)}
                onAudioUrl={url => setReasonAudioUrl(url)}
              />
            </div>
            {reasonAudioUrl && (
              <div className="mt-1.5">
                <AudioPlayer src={reasonAudioUrl} label="Voice reason" />
              </div>
            )}
            <p className="text-[9px] text-slate-400 mt-1">Your edits and reasoning are stored to improve future AI prescriptions for similar cases.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="save-edited-prescription">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Prescription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProcedurePicker({ onPick }) {
  const [procedures, setProcedures] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/knowledge-base/procedures/list`, { withCredentials: true })
      .then(({ data }) => setProcedures(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);
  if (procedures.length === 0) {
    return <p className="mt-1 text-[10px] text-muted-foreground">No procedures in library yet — type below or add templates in Knowledge Base</p>;
  }
  return (
    <Select value="" onValueChange={(v) => v && onPick(v)}>
      <SelectTrigger className="mt-1 h-8 text-xs" data-testid="procedure-picker">
        <SelectValue placeholder="+ Add from procedure library" />
      </SelectTrigger>
      <SelectContent>
        {procedures.map(p => (
          <SelectItem key={p.id} value={p.name} className="text-xs">
            {p.name}{p.category ? ` (${p.category})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ConfidenceBadge({ confidence }) {
  if (!confidence) return null;
  const colors = {
    high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <Badge variant="outline" className={`text-[10px] gap-1 ${colors[confidence.level] || colors.medium}`} data-testid="confidence-badge">
      <span className={`w-1.5 h-1.5 rounded-full inline-block ${confidence.level === 'high' ? 'bg-emerald-500' : confidence.level === 'medium' ? 'bg-amber-500' : 'bg-red-500'}`} />
      {confidence.level?.toUpperCase()} ({confidence.score}%)
    </Badge>
  );
}

function ValidationWarnings({ validation }) {
  if (!validation || (validation.errors?.length === 0 && validation.warnings?.length === 0 && validation.blocked_medicines?.length === 0)) return null;
  return (
    <div className="space-y-1" data-testid="validation-warnings">
      {validation.blocked_medicines?.length > 0 && (
        <div className="p-1.5 rounded-md bg-red-50 border border-red-200">
          <p className="text-[9px] font-bold uppercase tracking-wider text-red-600 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Blocked Medicines (Removed)</p>
          {validation.blocked_medicines.map((b, i) => <p key={i} className="text-[10px] text-red-500 mt-0.5">{b.medicine_name}: {b.reason}</p>)}
        </div>
      )}
      {validation.warnings?.length > 0 && (
        <div className="p-1.5 rounded-md bg-amber-50 border border-amber-200">
          <p className="text-[9px] font-bold uppercase tracking-wider text-amber-600">Warnings</p>
          {validation.warnings.map((w, i) => <p key={i} className="text-[10px] text-amber-600 mt-0.5">{w}</p>)}
        </div>
      )}
    </div>
  );
}

function LibraryMediaPicker({ cardId, onAttached }) {
  const [items, setItems] = useState([]);
  const [attaching, setAttaching] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/media-library`, { withCredentials: true })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const attach = async (mediaId) => {
    setAttaching(true);
    try {
      await axios.post(`${API_URL}/api/consult-cards/${cardId}/media-library-attach`, { media_id: mediaId }, { withCredentials: true });
      toast.success('Media attached');
      onAttached?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to attach');
    } finally { setAttaching(false); }
  };

  return (
    <Select value="" onValueChange={(v) => v && attach(v)} disabled={attaching || items.length === 0}>
      <SelectTrigger className="h-6 w-auto min-w-[100px] text-[10px] rounded-full px-2.5 gap-1 border-slate-200" data-testid="library-media-picker">
        {attaching ? <Loader2 className="w-3 h-3 animate-spin" /> : <SelectValue placeholder={items.length === 0 ? 'Library empty' : '+ From Library'} />}
      </SelectTrigger>
      <SelectContent>
        {items.map(item => (
          <SelectItem key={item.id} value={item.id} className="text-xs">
            {item.title || item.patient_facing_title || item.url}{item.media_type ? ` · ${item.media_type}` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MediaAttachments({ card, onRefresh }) {
  const { user } = useAuth();
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const canEdit = user?.role === 'doctor' || user?.role === 'admin';
  const media = card.media_attachments || [];
  const links = card.media_links || [];

  const handleDelete = async (filename) => {
    try {
      await axios.delete(`${API_URL}/api/consult-cards/${card.id}/media/${filename}`, { withCredentials: true });
      toast.success('Media removed');
      onRefresh?.();
    } catch { toast.error('Failed to remove'); }
  };

  const handleAddLink = async () => {
    if (!linkUrl.trim()) return;
    setAddingLink(true);
    try {
      await axios.post(`${API_URL}/api/consult-cards/${card.id}/media-links`, { url: linkUrl.trim(), title: linkTitle.trim() }, { withCredentials: true });
      toast.success('Link added');
      setLinkUrl(''); setLinkTitle(''); setShowLinkInput(false);
      onRefresh?.();
    } catch { toast.error('Failed to add link'); }
    finally { setAddingLink(false); }
  };

  const handleRemoveLink = async (url) => {
    try {
      await axios.delete(`${API_URL}/api/consult-cards/${card.id}/media-links`, { data: { url }, withCredentials: true });
      toast.success('Link removed');
      onRefresh?.();
    } catch { toast.error('Failed'); }
  };

  return (
    <Card className="border-slate-200" data-testid="media-attachments">
      <CardHeader className="pb-1 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-heading flex items-center gap-2 text-slate-700">
            <Eye className="w-4 h-4 text-slate-500" /> Media &amp; Links ({media.length + links.length})
          </CardTitle>
          {canEdit && (
            <div className="flex gap-1">
              <LibraryMediaPicker cardId={card.id} onAttached={onRefresh} />
              <Button size="sm" variant="outline" onClick={() => setShowLinkInput(!showLinkInput)} className="h-6 text-[10px] rounded-full gap-1" data-testid="add-link-btn">
                <Plus className="w-3 h-3" /> Link
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2">
        {/* Add link input */}
        {showLinkInput && (
          <div className="p-2 rounded-lg bg-slate-50 border border-slate-200 space-y-1.5" data-testid="link-input-section">
            <input type="url" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="Paste YouTube, Instagram, or any URL..." className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 focus:outline-none focus:border-emerald-400" data-testid="link-url-input" />
            <input type="text" value={linkTitle} onChange={e => setLinkTitle(e.target.value)} placeholder="Title (optional)" className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 focus:outline-none focus:border-emerald-400" />
            <div className="flex gap-1">
              <Button size="sm" onClick={handleAddLink} disabled={addingLink || !linkUrl.trim()} className="h-6 text-[9px] rounded-full bg-[#0E5132] gap-1" data-testid="save-link-btn">
                {addingLink ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Save className="w-2.5 h-2.5" />} Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowLinkInput(false); setLinkUrl(''); setLinkTitle(''); }} className="h-6 text-[9px] rounded-full">Cancel</Button>
            </div>
          </div>
        )}

        {/* Links */}
        {links.map((link, i) => (
          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 border border-blue-200" data-testid={`media-link-${i}`}>
            <div className="flex-1 min-w-0">
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-[11px] font-medium text-blue-700 hover:underline truncate block">{link.title || link.url}</a>
              <p className="text-[9px] text-blue-400 truncate">{link.url}</p>
            </div>
            {canEdit && (
              <button onClick={() => handleRemoveLink(link.url)} className="p-0.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`remove-link-${i}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}

        {/* Files */}
        {media.length === 0 && links.length === 0 ? (
          <p className="text-[10px] text-slate-400 text-center py-2">No media or links attached</p>
        ) : media.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {media.map((m, i) => {
              const isImage = m.content_type?.startsWith('image/');
              const src = m.url?.startsWith('http') ? m.url : `${API_URL}${m.url}`;
              return (
                <div key={i} className="relative group rounded-lg border border-slate-200 overflow-hidden" data-testid={`media-item-${i}`}>
                  {isImage ? (
                    <a href={src} target="_blank" rel="noopener noreferrer">
                      <img src={src} alt={m.original_name} className="w-full h-20 object-cover" />
                    </a>
                  ) : (
                    <a href={src} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-20 bg-slate-50">
                      <FileText className="w-6 h-6 text-slate-400" />
                    </a>
                  )}
                  <p className="text-[8px] text-slate-500 px-1 py-0.5 truncate">{m.original_name}</p>
                  {canEdit && (
                    <button onClick={() => handleDelete(m.filename)} className="absolute top-0.5 right-0.5 p-0.5 rounded bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-media-${i}`}>
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProcedureSuggestionsPanel({ cardId, card, onRefresh }) {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteProcName, setNoteProcName] = useState('');
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editText, setEditText] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const canAct = user?.role === 'doctor' || user?.role === 'admin';
  const procedureNotes = card?.procedure_notes || [];

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/consult-cards/${cardId}/procedure-suggestions`, { withCredentials: true });
      setSuggestions(data.suggestions || []);
      setFetched(true);
    } catch { /* ok */ }
    finally { setLoading(false); }
  };

  const handleSaveNote = async (text, procName, audioUrl) => {
    if (!text?.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/consult-cards/${cardId}/procedure-notes`, {
        text: text.trim(),
        procedure_name: procName || '',
        audio_url: audioUrl || null,
      }, { withCredentials: true });
      toast.success('Procedure note saved');
      setNoteText(''); setNoteProcName(''); setShowAddNote(false);
      onRefresh?.();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleVoiceRecord = async () => {
    if (recording) {
      try { mediaRecorderRef.current?.stop(); } catch { /* ok */ }
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 48000 });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) { toast.error('Too short'); return; }
        setTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'procedure_note.webm');
          formData.append('procedure_name', noteProcName || '');
          const { data } = await axios.post(`${API_URL}/api/consult-cards/${cardId}/procedure-notes/voice?procedure_name=${encodeURIComponent(noteProcName || '')}`, formData, { withCredentials: true });
          if (data.summary) {
            toast.success('Voice note transcribed & summarized');
            setShowAddNote(false); setNoteText(''); setNoteProcName('');
            onRefresh?.();
          } else {
            toast.error(data.error || 'Transcription failed');
          }
        } catch { toast.error('Failed'); }
        finally { setTranscribing(false); }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const handleEditNote = async (note) => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/consult-cards/${cardId}/procedure-notes`, {
        text: editText.trim(),
        procedure_name: note.procedure_name,
      }, { withCredentials: true });
      toast.success('Note updated');
      setEditingNote(null); setEditText('');
      onRefresh?.();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API_URL}/api/consult-cards/${cardId}/procedure-notes/${noteId}`, { withCredentials: true });
      toast.success('Note removed');
      onRefresh?.();
    } catch { toast.error('Failed'); }
  };

  if (!canAct) return null;

  return (
    <Card className="border-violet-200 bg-violet-50/20" data-testid="procedure-suggestions-panel">
      <CardHeader className="pb-1 pt-3 px-4 cursor-pointer" onClick={() => { if(!fetched) fetchSuggestions(); setCollapsed(!collapsed); }}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-heading flex items-center gap-2 text-violet-700">
            <Stethoscope className="w-4 h-4 text-violet-500" />
            Procedure Suggested
            {procedureNotes.length > 0 && <Badge variant="outline" className="text-[9px] bg-violet-100 text-violet-600 border-violet-200">{procedureNotes.length} noted</Badge>}
          </CardTitle>
          <div className="flex items-center gap-1">
            {canAct && (
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setShowAddNote(!showAddNote); }} className="h-5 text-[9px] rounded-full gap-0.5 border-violet-200 text-violet-600 hover:bg-violet-100" data-testid="add-procedure-note-btn">
                <Plus className="w-2.5 h-2.5" /> Add
              </Button>
            )}
            {loading ? <Loader2 className="w-3 h-3 animate-spin text-violet-400" /> : collapsed ? <ChevronDown className="w-3.5 h-3.5 text-violet-400" /> : <ChevronUp className="w-3.5 h-3.5 text-violet-400" />}
          </div>
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent className="px-4 pb-3 space-y-2">
          {/* Add procedure note input */}
          {showAddNote && (
            <div className="p-2.5 rounded-lg bg-white border border-violet-200 space-y-1.5" data-testid="procedure-note-input">
              <input type="text" value={noteProcName} onChange={e => setNoteProcName(e.target.value)} placeholder="Procedure name (e.g., Chemical Peel)" className="w-full text-[11px] px-2 py-1.5 rounded border border-violet-200 focus:outline-none focus:border-violet-400 font-medium" data-testid="proc-name-input" />
              <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Describe the procedure details, aftercare instructions..." className="text-[11px] min-h-[50px] border-violet-200" data-testid="proc-text-input" />
              <div className="flex items-center gap-1.5">
                <Button size="sm" onClick={() => handleSaveNote(noteText, noteProcName)} disabled={saving || !noteText.trim()} className="h-6 text-[9px] rounded-full bg-violet-600 hover:bg-violet-700 gap-1" data-testid="save-proc-note-btn">
                  {saving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Save className="w-2.5 h-2.5" />} Save
                </Button>
                <Button size="sm" variant={recording ? "destructive" : "outline"} onClick={handleVoiceRecord} disabled={transcribing} className={`h-6 text-[9px] rounded-full gap-1 ${recording ? 'animate-pulse' : 'border-violet-200 text-violet-600'}`} data-testid="voice-proc-note-btn">
                  {transcribing ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : recording ? <><Square className="w-2.5 h-2.5" /> Stop</> : <><Mic className="w-2.5 h-2.5" /> Voice</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowAddNote(false); setNoteText(''); setNoteProcName(''); }} className="h-6 text-[9px] rounded-full">Cancel</Button>
              </div>
            </div>
          )}

          {/* Existing procedure notes */}
          {procedureNotes.map((note) => (
            <div key={note.id} className="p-2.5 rounded-lg bg-white border border-violet-100 space-y-1" data-testid={`proc-note-${note.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold text-slate-800">{note.procedure_name}</p>
                <div className="flex gap-0.5">
                  {canAct && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => { setEditingNote(note.id); setEditText(note.raw_text || note.summary); }} className="h-5 w-5 text-violet-500 hover:bg-violet-50"><Pencil className="w-2.5 h-2.5" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteNote(note.id)} className="h-5 w-5 text-red-400 hover:bg-red-50"><Trash2 className="w-2.5 h-2.5" /></Button>
                    </>
                  )}
                </div>
              </div>
              {editingNote === note.id ? (
                <div className="space-y-1">
                  <Textarea value={editText} onChange={e => setEditText(e.target.value)} className="text-[10px] min-h-[40px] border-violet-200" />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleEditNote(note)} disabled={saving} className="h-5 text-[8px] rounded-full bg-violet-600 gap-0.5"><Save className="w-2 h-2" /> Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingNote(null)} className="h-5 text-[8px] rounded-full">Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-[10px] text-slate-600">{note.summary}</p>
                  {note.aftercare && <p className="text-[9px] text-violet-600 italic">Aftercare: {note.aftercare}</p>}
                  {note.audio_url && <AudioPlayer src={note.audio_url} />}
                </>
              )}
              <p className="text-[8px] text-slate-400">{note.created_by}</p>
            </div>
          ))}

          {/* AI suggestions */}
          {fetched && suggestions.length > 0 && (
            <div className="pt-1 border-t border-violet-200">
              <p className="text-[9px] text-violet-500 font-bold uppercase tracking-wider mb-1">AI Suggestions</p>
              {suggestions.map((s, i) => (
                <div key={i} className="p-2 rounded-lg bg-violet-50 border border-violet-100 mb-1" data-testid={`proc-suggestion-${i}`}>
                  <p className="text-[10px] font-medium text-slate-700">{s.procedure}</p>
                  <p className="text-[9px] text-slate-500">{s.indication}</p>
                  <Button size="sm" variant="outline" onClick={() => { setNoteProcName(s.procedure); setNoteText(s.notes || s.indication || ''); setShowAddNote(true); }}
                    className="h-5 text-[8px] rounded-full mt-1 border-violet-200 text-violet-600 gap-0.5" data-testid={`use-suggestion-${i}`}>
                    <Plus className="w-2 h-2" /> Add as Note
                  </Button>
                </div>
              ))}
            </div>
          )}

          {!fetched && procedureNotes.length === 0 && !showAddNote && (
            <p className="text-[10px] text-violet-400 text-center py-1">Click to load AI suggestions or add a procedure note</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function emptyPrescription() {
  return {
    morning_routine: { medicines: [], notes: '' },
    evening_routine: { medicines: [], notes: '' },
    oral_medicines: { medicines: [], notes: '' },
    procedures: '',
    precautions: '',
    follow_up_date_suggestion: '',
  };
}

function DoctorReasoningSection({ card, onRefresh }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [reasoning, setReasoning] = useState(card.doctor_reasoning || '');
  const [saving, setSaving] = useState(false);
  const canEdit = user?.role === 'doctor' || user?.role === 'admin';

  if (!card.ai_prescription_draft || card.ai_prescription_draft?.insufficient_data) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/consult-cards/${card.id}/doctor-reasoning`, { doctor_reasoning: reasoning }, { withCredentials: true });
      toast.success('Reasoning saved');
      setEditing(false);
      onRefresh?.();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-t border-slate-200 pt-2 mt-2" data-testid="doctor-reasoning-section">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Doctor&apos;s Notes
        </p>
        {canEdit && !editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-5 text-[9px] text-blue-600 hover:bg-blue-50 px-1.5 rounded" data-testid="edit-reasoning-btn">
            <Pencil className="w-2.5 h-2.5 mr-0.5" /> {card.doctor_reasoning ? 'Edit' : 'Add'}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="space-y-1.5">
          <Textarea value={reasoning} onChange={e => setReasoning(e.target.value)} placeholder="Add your reasoning, notes, or observations..." className="text-[11px] min-h-[50px] border-slate-200" data-testid="reasoning-input" />
          <div className="flex gap-1">
            <Button size="sm" onClick={handleSave} disabled={saving} className="h-6 text-[10px] rounded-full bg-[#0E5132] gap-1">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setReasoning(card.doctor_reasoning || ''); }} className="h-6 text-[10px] rounded-full">Cancel</Button>
          </div>
        </div>
      ) : card.doctor_reasoning ? (
        <p className="text-[11px] text-slate-600 whitespace-pre-wrap">{card.doctor_reasoning}</p>
      ) : (
        <p className="text-[10px] text-amber-500 italic">No reasoning added yet</p>
      )}
      {card.doctor_reasoning_by && (
        <p className="text-[9px] text-slate-400 mt-1">— {card.doctor_reasoning_by}{card.doctor_reasoning_at ? `, ${formatDateIST(card.doctor_reasoning_at)}` : ''}</p>
      )}
    </div>
  );
}

function PrescriptionPanel({ card, onRefresh }) {
  const { user } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [localDraft, setLocalDraft] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const draft = card.ai_prescription_draft;
  const canEdit = user?.role === 'doctor' || user?.role === 'admin';

  // Fetch medicines for dropdown
  useEffect(() => {
    const fetchMedicines = async () => {
      try {
        // Fetch from both medicines and pharmacy collections
        const [medRes, pharmRes] = await Promise.all([
          axios.get(`${API_URL}/api/medicines`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/pharmacy/medicines?limit=500`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        // Merge: pharmacy medicines as {id, name, generic_name, brand}
        const pharmItems = pharmRes.data?.items || pharmRes.data || [];
        const pharmMeds = (Array.isArray(pharmItems) ? pharmItems : []).map(m => ({
          id: m.id || m.sku,
          name: m.name,
          generic_name: m.active_ingredients || '',
          brand: m.company || '',
          in_stock: m.in_stock,
          stock_qty: m.stock_qty,
          is_archived: m.is_archived,
          archive_reason: m.archive_reason,
        }));
        // Deduplicate by name (prefer clinic medicines over pharmacy)
        const clinicNames = new Set((medRes.data || []).map(m => m.name.toLowerCase()));
        const merged = [
          ...(medRes.data || []),
          ...pharmMeds.filter(m => !clinicNames.has(m.name.toLowerCase())),
        ];
        setMedicines(merged);
      } catch { /* ok */ }
    };
    fetchMedicines();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/api/consult-cards/${card.id}/generate-prescription`, {}, { withCredentials: true });
      toast.success('Prescription generated');
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const handleSaveEdit = async (editedData) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/consult-cards/${card.id}/prescription`, editedData, { withCredentials: true });
      toast.success('Prescription saved');
      if (data.draft_protocol_created) {
        toast.info('Draft protocol auto-created for this condition');
      }
      onRefresh?.();
      return data;
    } catch { toast.error('Save failed'); return null; }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prescription_${card.patient_name.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
    finally { setDownloading(false); }
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url);
    } catch { toast.error('Failed'); }
  };

  const renderMedTable = (section, title) => {
    const meds = draft?.[section]?.medicines || [];
    if (!meds.length) return null;
    return (
      <div className="mb-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-0.5 mb-1">{title}</p>
        <table className="w-full">
          <thead><tr className="text-[9px] uppercase tracking-wider text-slate-400">
            <th className="text-left py-0.5 pr-1 w-5">#</th><th className="text-left py-0.5 pr-1">Medicine</th><th className="text-left py-0.5 pr-1">Freq</th><th className="text-left py-0.5">Instructions — Duration</th>
          </tr></thead>
          <tbody>{meds.map((med, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              <td className="py-1 pr-1 text-[11px] text-slate-400 align-top">{i + 1}.</td>
              <td className="py-1 pr-1 text-[11px] font-medium text-slate-800 align-top">
                {med.medicine_name}
                {med.rationale && <p className="text-[9px] text-blue-500 font-normal italic mt-0.5">{med.rationale}</p>}
              </td>
              <td className="py-1 pr-1 text-[11px] text-slate-600 align-top font-mono">{med.frequency}</td>
              <td className="py-1 text-[11px] text-slate-500 align-top">{[med.instructions, med.duration].filter(Boolean).join(' — ')}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  };

  const isInsufficient = draft?.insufficient_data;
  const confidence = draft?._confidence;
  const validation = draft?._validation;
  const evaluation = draft?._evaluation;

  return (
    <>
      <Card className="border-slate-200 bg-white shadow-sm h-full" data-testid="prescription-panel">
        <div className="bg-[#0E5132] text-white px-4 py-2.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold tracking-wide truncate">{card.patient_name}</p>
              <p className="text-[9px] text-emerald-200">{card.patient_country_code} {card.patient_phone} &middot; {todayDateIST()}</p>
            </div>
            <div className="flex items-center gap-1.5">
              {card.needs_attention && (
                <Badge className="text-[8px] bg-amber-500 text-white border-0 animate-pulse">Needs Reasoning</Badge>
              )}
              {confidence && <ConfidenceBadge confidence={confidence} />}
              {canEdit && draft && !draft.parse_error && !isInsufficient && (
                <Button onClick={() => { setLocalDraft(JSON.parse(JSON.stringify(draft))); setEditOpen(true); }} size="sm" variant="ghost" className="h-6 rounded-full gap-1 text-[10px] text-white hover:bg-white/10" data-testid="edit-prescription-btn">
                  <Pencil className="w-3 h-3" /> Edit
                </Button>
              )}
            </div>
          </div>
        </div>
        <CardContent className="p-3 space-y-2 overflow-y-auto max-h-[calc(100vh-340px)]">
          {generating ? (
            <div className="text-center py-8 space-y-4" data-testid="generating-prescription">
              <div className="relative mx-auto w-12 h-12">
                <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                <Sparkles className="w-5 h-5 text-emerald-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Generating Prescription</p>
                <p className="text-xs text-muted-foreground mt-1">Analyzing transcript, protocols, and patient history...</p>
              </div>
            </div>
          ) : !draft ? (
            <div className="text-center py-6 space-y-3">
              <Pill className="w-7 h-7 text-slate-300 mx-auto" />
              <p className="text-xs text-muted-foreground">No prescription yet</p>
              {canEdit && (
                <div className="flex flex-col gap-2">
                  <Button onClick={handleGenerate} disabled={generating} size="sm" className="rounded-full gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="generate-prescription-btn">
                    <Sparkles className="w-3 h-3" /> Generate Prescription
                  </Button>
                  <Button onClick={() => { setLocalDraft(emptyPrescription()); setEditOpen(true); }} size="sm" variant="outline" className="rounded-full gap-1.5 text-xs" data-testid="create-manual-prescription-btn">
                    <Pencil className="w-3 h-3" /> Create Manually
                  </Button>
                </div>
              )}
            </div>
          ) : isInsufficient ? (
            <div className="text-center py-6 space-y-3" data-testid="insufficient-data-warning">
              <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
              <p className="text-sm font-medium text-amber-700">Insufficient Data</p>
              <p className="text-xs text-amber-600 max-w-xs mx-auto">{draft.message || 'Insufficient data for reliable recommendation.'}</p>
              {canEdit && (
                <div className="flex flex-col gap-2">
                  <Button onClick={handleGenerate} disabled={generating} variant="outline" size="sm" className="rounded-full gap-1.5 text-xs" data-testid="retry-generate-btn">
                    <RefreshCw className="w-3 h-3" /> Retry
                  </Button>
                  <Button onClick={() => { setLocalDraft(emptyPrescription()); setEditOpen(true); }} size="sm" variant="outline" className="rounded-full gap-1.5 text-xs" data-testid="create-manual-insufficient-btn">
                    <Pencil className="w-3 h-3" /> Create Manually
                  </Button>
                </div>
              )}
            </div>
          ) : draft.parse_error ? (
            <div className="text-center py-6 space-y-3" data-testid="parse-error-warning">
              <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
              <p className="text-sm font-medium text-amber-700">Couldn't Generate Prescription</p>
              <p className="text-xs text-amber-600 max-w-xs mx-auto">The AI response couldn't be read. Please retry or create the prescription manually.</p>
              {canEdit && (
                <div className="flex flex-col gap-2">
                  <Button onClick={handleGenerate} disabled={generating} variant="outline" size="sm" className="rounded-full gap-1.5 text-xs" data-testid="retry-generate-btn-parse">
                    <RefreshCw className="w-3 h-3" /> Retry
                  </Button>
                  <Button onClick={() => { setLocalDraft(emptyPrescription()); setEditOpen(true); }} size="sm" variant="outline" className="rounded-full gap-1.5 text-xs" data-testid="create-manual-parse-btn">
                    <Pencil className="w-3 h-3" /> Create Manually
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Evaluation status bar */}
              {evaluation && (
                <div className={`flex items-center gap-2 p-1.5 rounded-md border text-[10px] ${
                  evaluation.status === 'pass' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'
                }`} data-testid="evaluation-bar">
                  {evaluation.status === 'pass' ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                  <span className="font-medium">Quality: {evaluation.quality_score}%</span>
                  {evaluation.checks?.length > 0 && <span className="text-slate-400">&middot; {evaluation.checks[0]}</span>}
                </div>
              )}

              {/* Confidence requires manual review */}
              {confidence?.requires_manual_review && (
                <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-amber-50 border border-amber-200" data-testid="manual-review-warning">
                  <AlertTriangle className="w-3 h-3 text-amber-600 flex-shrink-0" />
                  <p className="text-[9px] text-amber-700 font-medium">Low confidence — Manual review strongly recommended</p>
                </div>
              )}

              {draft._learning_metadata?.patterns_used && (
                <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-violet-50 border border-violet-200">
                  <BrainCircuit className="w-3 h-3 text-violet-600 flex-shrink-0" />
                  <p className="text-[9px] text-violet-600">Personalized to clinic protocols &amp; learned preferences</p>
                </div>
              )}

              {/* Retrieval metadata */}
              {draft._retrieval_metadata && (
                <div className="flex items-center gap-2 text-[9px] text-slate-400">
                  <span>{draft._retrieval_metadata.protocols_found} protocol(s)</span>
                  <span>&middot;</span>
                  <span>{draft._retrieval_metadata.similar_cases_found} similar case(s)</span>
                  <span>&middot;</span>
                  <span>{draft._retrieval_metadata.inventory_count} medicines</span>
                </div>
              )}

              <ValidationWarnings validation={validation} />

              <div className="border border-slate-200 rounded-lg p-3 bg-white">
                <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-2"><FileText className="w-3 h-3" /> Rx</div>
                {renderMedTable('morning_routine', 'Morning Routine')}
                {renderMedTable('evening_routine', 'Evening Routine')}
                {renderMedTable('oral_medicines', 'Oral Medicines')}
                {draft.probable_diagnosis && (draft.probable_diagnosis.tags?.length > 0 || draft.probable_diagnosis.notes) && (
                  <div className="mb-2 p-2 rounded-lg bg-blue-50/50 border border-blue-100">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600 mb-1">Provisional Diagnosis</p>
                    {draft.probable_diagnosis.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1">{draft.probable_diagnosis.tags.map((tag, i) => (
                        <span key={i} className="inline-flex text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{tag}</span>
                      ))}</div>
                    )}
                    {draft.probable_diagnosis.notes && <p className="text-[11px] text-slate-600">{draft.probable_diagnosis.notes}</p>}
                  </div>
                )}
                {draft.procedures && draft.procedures !== 'None' && (
                  <div className="mb-2"><p className="text-[10px] font-bold uppercase tracking-wider text-violet-600">Procedure Suggested</p><p className="text-[11px] text-slate-700" data-testid="draft-procedures">{draft.procedures}</p></div>
                )}
                {draft.precautions && draft.precautions !== 'None' && (
                  <div className="mb-2"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Precautions</p><p className="text-[11px] text-slate-700">{draft.precautions}</p></div>
                )}
                {draft.follow_up_date_suggestion && (
                  <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Follow-up</p><p className="text-[11px] text-slate-700">{formatFollowUpDate(draft.follow_up_date_suggestion)}</p></div>
                )}
              </div>

              {/* Prescription Actions: View PDF, Print, Share */}
              <div className="flex items-center gap-2 border-t border-slate-200 pt-2 mt-2" data-testid="rx-actions-bar">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 flex items-center gap-1 mr-auto">
                  <Pill className="w-3 h-3" /> Prescription
                </p>
                <button
                  onClick={() => window.open(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf`, '_blank')}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-full transition-colors"
                  data-testid="rx-view-pdf"
                >
                  <Eye className="w-3 h-3" /> View PDF
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf?print_mode=true`, { credentials: 'include' });
                      const blob = await response.blob();
                      const url = URL.createObjectURL(blob);
                      const printFrame = document.createElement('iframe');
                      printFrame.style.position = 'fixed';
                      printFrame.style.top = '-9999px';
                      printFrame.style.left = '-9999px';
                      printFrame.src = url;
                      document.body.appendChild(printFrame);
                      printFrame.onload = () => {
                        setTimeout(() => {
                          printFrame.contentWindow.print();
                          setTimeout(() => { document.body.removeChild(printFrame); URL.revokeObjectURL(url); }, 2000);
                        }, 300);
                      };
                    } catch { toast.error('Failed to load prescription for printing'); }
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-full transition-colors"
                  data-testid="rx-print"
                >
                  <Printer className="w-3 h-3" /> Print
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf`, { credentials: 'include' });
                      const blob = await response.blob();
                      const file = new File([blob], `prescription_${card.patient_name?.replace(/\s+/g, '_')}.pdf`, { type: 'application/pdf' });
                      if (navigator.share && navigator.canShare?.({ files: [file] })) {
                        await navigator.share({ files: [file], title: `Prescription - ${card.patient_name}` });
                      } else if (navigator.share) {
                        const url = URL.createObjectURL(blob);
                        await navigator.share({ title: `Prescription - ${card.patient_name}`, url });
                        URL.revokeObjectURL(url);
                      } else {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = file.name;
                        a.click(); URL.revokeObjectURL(url);
                        toast.success('Prescription downloaded');
                      }
                    } catch (err) { if (err.name !== 'AbortError') toast.error('Sharing failed'); }
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-full transition-colors"
                  data-testid="rx-share"
                >
                  <Share2 className="w-3 h-3" /> Share
                </button>
              </div>

              {/* Doctor reasoning section */}
              <DoctorReasoningSection card={card} onRefresh={onRefresh} />

              {draft.allergy_warnings?.length > 0 && (
                <div className="p-2 rounded-lg bg-red-50 border border-red-200">
                  <div className="flex items-center gap-1 text-red-600 text-[10px] font-medium"><AlertTriangle className="w-3 h-3" /> Allergy Warnings</div>
                  {draft.allergy_warnings.map((w, i) => <p key={i} className="text-[10px] text-red-500 mt-0.5">{w}</p>)}
                </div>
              )}
              {draft.stock_warnings?.length > 0 && (
                <div className="p-2 rounded-lg bg-amber-50 border border-amber-200" data-testid="ai-stock-warnings">
                  <div className="flex items-center gap-1 text-amber-700 text-[10px] font-medium"><AlertTriangle className="w-3 h-3" /> Stock Warnings</div>
                  {draft.stock_warnings.map((w, i) => <p key={i} className="text-[10px] text-amber-700 mt-0.5">{w}</p>)}
                </div>
              )}
              <div className="flex gap-2 pt-2 border-t border-slate-200">
                <Button onClick={handleGenerate} disabled={generating} variant="outline" size="sm" className="rounded-full gap-1 text-[10px] flex-1 h-7" data-testid="regenerate-prescription-btn">
                  {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Regenerate
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <PrescriptionEditModal open={editOpen} onClose={() => { setEditOpen(false); setLocalDraft(null); }} draft={localDraft} onSave={handleSaveEdit} medicines={medicines} />
    </>
  );
}

function PrescriptionTrainingQuestions({ cardId, hasPrescription }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!hasPrescription || fetched) return;
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/consult-cards/${cardId}/prescription-training-questions`, { withCredentials: true });
        setQuestions(data.questions || []);
        setFetched(true);
      } catch { /* no questions */ }
      finally { setLoading(false); }
    };
    fetchQuestions();
  }, [cardId, hasPrescription, fetched]);

  // Re-fetch when prescription changes
  useEffect(() => { setFetched(false); }, [hasPrescription]);

  if (!hasPrescription) return null;
  if (!loading && questions.length === 0 && fetched) return null;

  return (
    <Card className="border-sky-200 bg-sky-50/30" data-testid="training-questions-panel">
      <CardHeader className="pb-1 pt-3 px-4 cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-heading flex items-center gap-2 text-sky-700">
            <GraduationCap className="w-4 h-4 text-sky-500" />
            AI Training Questions
            {questions.length > 0 && (
              <Badge variant="outline" className="text-[9px] bg-sky-100 text-sky-600 border-sky-200">{questions.length}</Badge>
            )}
          </CardTitle>
          {collapsed ? <ChevronDown className="w-3.5 h-3.5 text-sky-400" /> : <ChevronUp className="w-3.5 h-3.5 text-sky-400" />}
        </div>
        <p className="text-[9px] text-sky-500 mt-0.5">Gap analysis — help the AI learn from your prescribing decisions</p>
      </CardHeader>
      {!collapsed && (
        <CardContent className="px-4 pb-3 space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="w-3 h-3 animate-spin text-sky-500" />
              <span className="text-[10px] text-sky-600">Analyzing prescription vs protocols...</span>
            </div>
          ) : (
            questions.map((q, i) => (
              <div key={q.id || i} className="p-2 rounded-lg bg-white border border-sky-100" data-testid={`training-q-${q.id || i}`}>
                <p className="text-[11px] text-slate-700 font-medium">{q.question}</p>
                {q.purpose && <p className="text-[9px] text-sky-500 mt-0.5 italic">{q.purpose}</p>}
              </div>
            ))
          )}
        </CardContent>
      )}
    </Card>
  );
}

function OldPrescriptionsPanel({ previousVisits, patientName, patientPhone, patientAge, patientGender }) {
  const [viewPrescription, setViewPrescription] = useState(null);

  const visitsWithRx = (previousVisits || []).filter(v =>
    v.ai_prescription_draft && !v.ai_prescription_draft.parse_error && !v.ai_prescription_draft.insufficient_data
  );

  if (visitsWithRx.length === 0) return null;

  return (
    <>
      <Card className="border-slate-200 shadow-sm" data-testid="old-prescriptions-panel">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="text-xs font-heading flex items-center gap-2 text-slate-600">
            <History className="w-3.5 h-3.5 text-slate-400" />
            Previous Prescriptions
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 space-y-1.5">
          {visitsWithRx.map(visit => {
            const rx = visit.ai_prescription_draft;
            const allMeds = [];
            for (const sec of ['morning_routine', 'evening_routine', 'oral_medicines']) {
              const meds = rx[sec]?.medicines || [];
              meds.forEach(m => { if (m.medicine_name) allMeds.push(m.medicine_name); });
            }
            return (
              <button key={visit.id} onClick={() => setViewPrescription(visit)}
                className="w-full text-left p-2 rounded-lg border border-slate-100 bg-slate-50/50 hover:bg-slate-100 hover:border-slate-200 transition-colors cursor-pointer group"
                data-testid={`old-rx-${visit.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-3 h-3 text-slate-400" />
                    <span className="text-[10px] font-medium text-slate-700">
                      {visit.created_at ? formatDateIST(visit.created_at) : 'Unknown'}
                    </span>
                    {!visit.can_edit_prescription && <Lock className="w-2.5 h-2.5 text-slate-300" />}
                  </div>
                  <Eye className="w-3 h-3 text-slate-400 group-hover:text-blue-500 transition-colors" />
                </div>
                <p className="text-[9px] text-slate-500 mt-0.5 truncate">{allMeds.join(', ') || 'No medicines'}</p>
              </button>
            );
          })}
        </CardContent>
      </Card>

      <PrescriptionViewModal
        open={!!viewPrescription}
        onClose={() => setViewPrescription(null)}
        prescription={viewPrescription?.ai_prescription_draft}
        visitDate={viewPrescription?.created_at}
        canEdit={viewPrescription?.can_edit_prescription}
        patientName={patientName}
        patientPhone={patientPhone}
        patientAge={patientAge}
        patientGender={patientGender}
      />
    </>
  );
}

export default function ConsultCardDetailPage() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [creatingSession, setCreatingSession] = useState(false);
  const [closing, setClosing] = useState(false);
  const refreshTimerRef = useRef(null);

  const fetchCard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/consult-cards/${cardId}`, { withCredentials: true });
      setCard(data);
    } catch {
      toast.error('Failed to load');
      navigate('/dashboard');
    } finally { setLoading(false); }
  }, [cardId, navigate]);

  const fetchDoctors = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setDoctors(data.filter(u => u.role === 'doctor'));
    } catch { /* ok */ }
  }, []);

  useEffect(() => {
    fetchCard();
    fetchDoctors();
    refreshTimerRef.current = setInterval(fetchCard, 5000);
    return () => clearInterval(refreshTimerRef.current);
  }, [fetchCard, fetchDoctors]);

  const handleAddSession = async (type) => {
    setCreatingSession(true);
    try {
      await axios.post(`${API_URL}/api/consult-cards/${cardId}/sessions`, { session_type: type }, { withCredentials: true });
      fetchCard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCreatingSession(false); }
  };

  const handleAssignDoctor = async (doctorId) => {
    const doc = doctors.find(d => d.id === doctorId);
    try {
      await axios.put(`${API_URL}/api/consult-cards/${cardId}`, { assigned_doctor_id: doctorId, assigned_doctor_name: doc?.name || '' }, { withCredentials: true });
      fetchCard();
    } catch { toast.error('Failed'); }
  };

  const handleCloseCard = async () => {
    setClosing(true);
    try {
      await axios.put(`${API_URL}/api/consult-cards/${cardId}`, { stage: 'closed' }, { withCredentials: true });
      toast.success('Visit closed');
      navigate('/dashboard');
    } catch { toast.error('Failed'); }
    finally { setClosing(false); }
  };

  const handleDeleteCard = async () => {
    if (!window.confirm(`Permanently delete this consult card for "${card?.patient_name}"?\n\nThis will remove the card, all sessions, recordings, and media. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/consult-cards/${cardId}`, { withCredentials: true });
      toast.success('Consult card deleted');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0E5132]" /></div>;
  if (!card) return null;

  const historySessions = card.sessions?.filter(s => s.session_type === 'history') || [];
  const consultSessions = card.sessions?.filter(s => s.session_type === 'doctor_consult') || [];
  const isClosed = card.stage === 'closed';

  return (
    <div className="space-y-4" data-testid="consult-card-detail">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="rounded-full h-8 w-8 shrink-0" data-testid="back-button"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-heading font-bold text-slate-800 truncate" data-testid="card-patient-name">{card.patient_name}</h1>
              <Badge variant="outline" className="gap-1 text-[10px] shrink-0"><Phone className="w-2.5 h-2.5" /> {card.patient_phone}</Badge>
            </div>
            <StageProgress currentStage={card.stage} />
          </div>
        </div>
        <div className="flex items-center gap-2 ml-10 sm:ml-0 flex-wrap">
          {doctors.length > 0 && !isClosed && (
            <Select value={card.assigned_doctor_id || ''} onValueChange={handleAssignDoctor}>
              <SelectTrigger className="w-36 sm:w-44 rounded-xl text-xs h-8" data-testid="assign-doctor-select"><SelectValue placeholder="Assign Doctor" /></SelectTrigger>
              <SelectContent>{doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {card.assigned_doctor_name && !doctors.length && (
            <Badge variant="outline" className="gap-1 border-violet-200 bg-violet-50 text-violet-700 text-xs"><Stethoscope className="w-3 h-3" /> {card.assigned_doctor_name}</Badge>
          )}
          {!isClosed && (card.stage === 'prescription' || card.stage === 'doctor_consult') && (
            <Button onClick={handleCloseCard} disabled={closing} size="sm" className="rounded-full gap-1 bg-slate-800 hover:bg-slate-900 h-8 text-xs" data-testid="close-card-btn">
              {closing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Close
            </Button>
          )}
          {user?.role === 'admin' && (
            <Button onClick={handleDeleteCard} variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50" title="Delete this consult card" data-testid="delete-card-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-4 order-2 lg:order-1">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-heading font-semibold text-amber-700 flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> History ({historySessions.length})</h2>
              {!isClosed && <Button onClick={() => handleAddSession('history')} disabled={creatingSession} variant="outline" size="sm" className="rounded-full gap-1 text-[10px] border-amber-200 text-amber-600 h-7" data-testid="add-history-session"><Plus className="w-3 h-3" /> Add</Button>}
            </div>
            {historySessions.length === 0 ? (
              <Card className="border-dashed border-amber-200 bg-amber-50/20"><CardContent className="py-4 text-center"><p className="text-[11px] text-muted-foreground">No history sessions yet.</p></CardContent></Card>
            ) : (
              <div className="space-y-2">{historySessions.map(s => <SessionCard key={s.id} session={s} cardId={cardId} onRefresh={fetchCard} />)}</div>
            )}
          </div>
          {/* Doctor's Brief — consolidated summary of all history sessions */}
          <DoctorsBriefPanel cardId={cardId} historySessions={historySessions} />
          {/* Suggested Questions — appears after history sessions have summaries */}
          <SuggestedQuestionsPanel cardId={cardId} sessions={card.sessions} />
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-heading font-semibold text-violet-700 flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" /> Doctor Consult ({consultSessions.length})</h2>
              {!isClosed && <Button onClick={() => handleAddSession('doctor_consult')} disabled={creatingSession} variant="outline" size="sm" className="rounded-full gap-1 text-[10px] border-violet-200 text-violet-600 h-7" data-testid="add-consult-session"><Plus className="w-3 h-3" /> Add</Button>}
            </div>
            {consultSessions.length === 0 ? (
              <Card className="border-dashed border-violet-200 bg-violet-50/20"><CardContent className="py-4 text-center"><p className="text-[11px] text-muted-foreground">No doctor consult sessions yet.</p></CardContent></Card>
            ) : (
              <div className="space-y-2">{consultSessions.map(s => <SessionCard key={s.id} session={s} cardId={cardId} onRefresh={fetchCard} />)}</div>
            )}
          </div>
          {/* Previous visits with summaries */}
          <PreviousVisitsPanel previousVisits={card.previous_visits} edenVisits={card.eden_visits} patientName={card.patient_name} patientPhone={card.patient_phone} />
        </div>
        <div className="lg:col-span-2 order-1 lg:order-2">
          <div className="lg:sticky lg:top-4 space-y-4">
            <PrescriptionPanel card={card} onRefresh={fetchCard} />
            {/* Procedure suggestions */}
            <ProcedureSuggestionsPanel cardId={cardId} card={card} onRefresh={fetchCard} />
            {/* AI Training Questions — gap analysis below prescription */}
            <PrescriptionTrainingQuestions cardId={cardId} hasPrescription={!!card.ai_prescription_draft && !card.ai_prescription_draft?.insufficient_data && !card.ai_prescription_draft?.parse_error} />
            {/* Media attachments */}
            <MediaAttachments card={card} onRefresh={fetchCard} />
            {/* Old prescriptions from previous visits */}
            <OldPrescriptionsPanel previousVisits={card.previous_visits} patientName={card.patient_name} patientPhone={card.patient_phone} />
          </div>
        </div>
      </div>
    </div>
  );
}
